import { neueBestellungOeffnen } from '@/lib/neueBestellung';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  BookLock,
  ExternalLink,
  LayoutDashboard,
  Settings,
  Plus,
  CircleHelp,
  PanelLeftClose,
  PanelLeftOpen
} from 'lucide-react';
import { clsx } from 'clsx';
import { Button } from '@/components/ui/Button';
import { useSettings } from '@/hooks/useSettings';
import { HilfePanel } from '@/onboarding/HilfePanel';
import { portalAdresse } from '@/lib/portal';

type NavItem = { to: string; label: string; icon: typeof LayoutDashboard };

const NAV: readonly NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  // Das Archiv steht direkt unter dem Dashboard und nicht unten bei den
  // Einstellungen: es ist eine Liste von Bestellungen, kein Werkzeug.
  // Wer eine alte Bestellung sucht, sucht sie neben der Liste der neuen.
  { to: '/archiv', label: 'Archiv', icon: Archive },
  // „Reserviert?" steht bei den Listen und nicht bei den Einstellungen:
  // Es ist eine Frage an den Bestand, die am Tresen gestellt wird, und
  // sie soll EINEN Handgriff vom Dashboard entfernt sein (A27.3).
  { to: '/reserviert', label: 'Reserviert?', icon: BookLock },
  // Kanban ist noch nicht spezifiziert (SPEC-009). Die Route existiert
  // als Platzhalter; ein Menuepunkt, der auf eine leere Seite fuehrt,
  // kostet nur Vertrauen. Wieder einblenden, sobald die Ansicht steht.
  // { to: '/kanban', label: 'Kanban', icon: Columns3 },
  { to: '/einstellungen', label: 'Einstellungen', icon: Settings }
];

/**
 * Wo die Vorliebe für die Leistenbreite liegt.
 *
 * localStorage ist hier ausdrücklich erlaubt (CLAUDE.md: für
 * UI-Vorlieben ja, für Auth-Daten nein). Und der Unterschied zur Klappe
 * aus A16.1 ist beabsichtigt: dort wird ein Formularteil aufgeklappt,
 * der bei JEDER Erfassung wieder zu sein soll; hier geht es um eine
 * Vorliebe für die ganze Oberfläche, die man einmal einstellt.
 */
const SPEICHER_SCHLUESSEL = 'te-seitenleiste-offen';

/**
 * Nur die ausdrückliche „1" gilt als aufgeklappt.
 *
 * Damit ist jeder andere Fall — Schlüssel fehlt, Wert ist „true", „ja",
 * leer oder Unfug, oder localStorage wirft überhaupt (privates Fenster,
 * gesperrte Website-Daten) — automatisch EINGEKLAPPT. Der Zweck dieses
 * Pakets ist der Platz; im Zweifel gibt es ihn her.
 */
export function seitenleisteOffenGespeichert(): boolean {
  try {
    return window.localStorage.getItem(SPEICHER_SCHLUESSEL) === '1';
  } catch {
    return false;
  }
}

/** Gemeinsame Form für alle Ziele der Leiste — 44 Pixel in jeder Lage. */
function zielKlasse(offen: boolean): string {
  return clsx(
    'flex min-h-[44px] min-w-[44px] items-center rounded text-sm text-ink-soft',
    'transition-colors hover:bg-cream-dark hover:text-ink',
    offen ? 'gap-3 px-3 py-2' : 'justify-center py-2'
  );
}

/**
 * Das aktive Ziel — Fläche UND Rahmen, nicht nur Farbe.
 *
 * Ein reiner Farbunterschied verschwindet für alle, die Farben schlecht
 * unterscheiden; `akzent-sanft` auf `cream-warm` ist zudem auch in
 * Graustufen ein schwacher Unterschied. Der Rahmen ist eine Kante, die
 * es vorher nicht gab — die sieht man unabhängig vom Farbsehen.
 */
const AKTIV_KLASSE = 'bg-akzent-sanft text-akzent-stark ring-1 ring-akzent';

/**
 * Der Name eines Ziels, wenn nur das Symbol dasteht.
 *
 * WARUM HIER KEINE SPRECHBLASE AUF TIPP HÄNGT
 *   Die Notiz-Marke aus A12.6 zeigt ihre Blase beim Antippen — sie ist
 *   selbst kein Ziel, ein Tipp darauf hat sonst nichts zu tun. Ein
 *   Navigations-Symbol IST das Ziel: ein Tipp darauf springt. Eine
 *   Blase, die sich davorschiebt, kostet entweder den Sprung oder einen
 *   zweiten Tipp für jede Navigation — genau die Reibung, die A12 und
 *   A14 abgebaut haben.
 *
 *   Der Weg zum Namen ohne Überfahren ist deshalb der Aufklapp-Schalter:
 *   EIN Tipp, und alle sieben Namen stehen da, nicht nur einer. Dazu
 *   trägt jedes Ziel sein `aria-label`, damit Vorlesewerkzeuge es auch
 *   eingeklappt benennen können.
 *
 *   Die Blase hier erscheint beim Überfahren und beim Tastatur-Fokus —
 *   als Zugabe für Maus und Tastatur, nicht als einziger Weg.
 *
 * Der Fallstrick aus A12.6 gilt auch hier: beim Rollen wird die Blase
 * NACHGEFÜHRT statt geschlossen. Ein Fokus-Sprung rollt den Kasten, und
 * das `scroll`-Ereignis kommt erst nach dem `focus`, das die Blase
 * gerade geöffnet hat.
 */
function MitNamen({
  name,
  zeigen,
  children
}: {
  name: string;
  zeigen: boolean;
  children: ReactNode;
}) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const huelle = useRef<HTMLSpanElement>(null);

  function anHuelle(): { x: number; y: number } | null {
    const el = huelle.current;
    if (el === null) return null;
    const r = el.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight) return null;
    return { x: r.right + 8, y: r.top + r.height / 2 };
  }

  useEffect(() => {
    if (pos === null) return;
    const nachfuehren = () => setPos(anHuelle());
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPos(null);
    };
    window.addEventListener('scroll', nachfuehren, true);
    window.addEventListener('resize', nachfuehren);
    window.addEventListener('keydown', taste);
    return () => {
      window.removeEventListener('scroll', nachfuehren, true);
      window.removeEventListener('resize', nachfuehren);
      window.removeEventListener('keydown', taste);
    };
  }, [pos]);

  if (!zeigen) return <>{children}</>;

  return (
    <span
      ref={huelle}
      className="block"
      onMouseEnter={() => setPos(anHuelle())}
      onMouseLeave={() => setPos(null)}
      onFocus={() => setPos(anHuelle())}
      onBlur={() => setPos(null)}
    >
      {children}
      {pos !== null && (
        <span
          role="tooltip"
          style={{ left: pos.x, top: pos.y, transform: 'translateY(-50%)' }}
          className="fixed z-50 whitespace-nowrap rounded-md border border-line bg-cream px-2.5 py-1.5 text-xs text-ink shadow-sm"
        >
          {name}
        </span>
      )}
    </span>
  );
}

export function Sidebar() {
  const settings = useSettings(['portal_url', 'portal_label']);
  // Dieselbe Prüfung wie im Mobil-Menü: eine Adresse aus der
  // Einstellungs-Tabelle ist nichts, worauf man blind springt.
  const portalUrl = portalAdresse(settings.data?.settings.portal_url);
  const portalLabel = settings.data?.settings.portal_label ?? null;
  const navigate = useNavigate();
  const [hilfeOffen, setHilfeOffen] = useState(false);
  const [offen, setOffen] = useState(seitenleisteOffenGespeichert);

  useEffect(() => {
    try {
      window.localStorage.setItem(SPEICHER_SCHLUESSEL, offen ? '1' : '0');
    } catch {
      // Ein privates Fenster darf nichts speichern. Die Leiste
      // funktioniert trotzdem — sie merkt es sich nur nicht.
    }
  }, [offen]);

  return (
    // 56 Pixel eingeklappt statt 192 bzw. 224 aufgeklappt. Gemessen bei
    // 1024 px Fensterbreite belegte die Leiste vorher 22 Prozent des
    // Schirms — dauerhaft, für sechs Ziele, die man selten wechselt.
    // `shrink-0` bleibt: die Breite soll fest sein, nur eben klein.
    <aside
      data-tour="seitenleiste"
      className={clsx(
        'flex shrink-0 flex-col border-r border-line bg-cream-warm',
        offen ? 'w-48 md:w-56' : 'w-14'
      )}
    >
      <div className={clsx('flex items-center pt-4 pb-3', offen ? 'gap-2 px-4' : 'justify-center')}>
        {offen && (
          /* KEIN `truncate` (A20.6).
             ------------------------------------------------------------
             Der Name stand als „Buchhandlun…" da. Nachgemessen bei
             aufgeklappter Leiste: der Kopf hat 191 px Innenraum, davon
             nimmt der Schalter 44 und der Abstand 8 — für den Titel
             bleiben 139 px. „Buchhandlung Engel" braucht in Lora 20 px
             genau 191 px.

             Er passt also nicht einmal dann, wenn der Schalter ganz
             wiche: 191 auf 191 wäre Gleichstand, und Gleichstand ist
             kein Sitz (A12.12). Die Leiste dafür auf 275 px zu
             verbreitern gäbe genau den Platz zurück, den A18 gerade
             gewonnen hat.

             Also bricht der Name um. Zwei Zeilen sind für einen Laden-
             namen keine Verlegenheit — so steht er auch am Schild. Was
             dasteht, wird damit nicht abgeschnitten, und die Tabelle
             daneben behält ihre Breite. */
          <div className="min-w-0 flex-1">
            <div className="font-serif text-xl leading-tight text-ink">Buchhandlung Engel</div>
            <div className="text-xs uppercase tracking-wide text-ink-faint">Bestell-Tracking</div>
          </div>
        )}
        {/* Der Schalter ist zugleich der Weg zu den Namen: ein Tipp, und
            alle stehen da. Deshalb sitzt er oben und nicht unten — dort
            sucht man ihn nicht. */}
        <MitNamen name="Leiste aufklappen" zeigen={!offen}>
          <button
            type="button"
            aria-expanded={offen}
            aria-label={offen ? 'Leiste einklappen' : 'Leiste aufklappen'}
            onClick={() => setOffen((o) => !o)}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded text-ink-soft transition-colors hover:bg-cream-dark hover:text-ink"
          >
            {offen ? (
              <PanelLeftClose aria-hidden className="h-5 w-5" />
            ) : (
              <PanelLeftOpen aria-hidden className="h-5 w-5" />
            )}
          </button>
        </MitNamen>
      </div>

      <div className={clsx('pb-3', offen ? 'px-3' : 'px-1.5')}>
        {/* Eingeklappt bleibt der Knopf eine Handlung und wird kein Ziel:
            er trägt weiter die volle Akzentfläche, während die Ziele
            darunter nur Text tragen. Das Plus-Zeichen ist dasselbe. */}
        <MitNamen name="Neue Bestellung" zeigen={!offen}>
          <Button
            variant="primary"
            // `min-w-[44px]` ist nicht doppelt gemoppelt: `w-full` in
            // einem 56-px-Kasten mit je 6 px Polsterung ergab gemessen
            // 43 px — ein Pixel unter dem Hausmass, weil die Breite aus
            // einer Teilung stammt und abgerundet wird.
            className={clsx('w-full', !offen && 'min-w-[44px] px-0')}
            data-tour="neue-bestellung"
            aria-label="Neue Bestellung"
            onClick={() => {
              // TanStack Router cross-route Search-Reducer kann typseitig
              // nicht aufgeloest werden. Cast als never gibt der Library
              // freie Hand; der Reducer-Typ ist trotzdem dokumentiert.
              neueBestellungOeffnen(navigate as never);
            }}
          >
            <Plus className="h-4 w-4 shrink-0" />
            {offen && 'Neue Bestellung'}
          </Button>
        </MitNamen>
      </div>

      <nav className={clsx('flex flex-1 flex-col gap-0.5', offen ? 'px-2' : 'px-1.5')}>
        {NAV.map(({ to, label, icon: Icon }) => (
          <MitNamen key={to} name={label} zeigen={!offen}>
            <Link
              to={to}
              aria-label={label}
              className={zielKlasse(offen)}
              activeProps={{ className: AKTIV_KLASSE, 'aria-current': 'page' }}
              activeOptions={{ exact: to === '/' }}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {offen && <span>{label}</span>}
            </Link>
          </MitNamen>
        ))}

        {/* Hilfe steht bewusst IN der Navigation und nicht in einem
            Menü: wer sie sucht, sucht sie hier. Der Anker sitzt am
            Eintrag selbst, damit ihn ein späteres Mobil-Layout mit der
            Bottom-Navigation übernehmen kann, ohne die Tour anzufassen. */}
        <MitNamen name="Hilfe" zeigen={!offen}>
          <button
            type="button"
            data-tour="hilfe"
            aria-label="Hilfe"
            onClick={() => setHilfeOffen(true)}
            className={zielKlasse(offen)}
          >
            <CircleHelp className="h-4 w-4 shrink-0" />
            {offen && <span>Hilfe</span>}
          </button>
        </MitNamen>
      </nav>

      {/* Erst beim Öffnen einhängen: sonst hinge an jeder Seite eine
          Dialog-Mechanik, die niemand braucht. */}
      {hilfeOffen && <HilfePanel open onClose={() => setHilfeOffen(false)} />}

      {portalUrl && (
        // Auswärts-Symbol statt Pfeil nach links: der Link führt aus
        // der Anwendung heraus, nicht eine Ebene zurück.
        <MitNamen name={portalLabel ?? 'Portal'} zeigen={!offen}>
          <a
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={portalLabel ?? 'Portal'}
            className={clsx(zielKlasse(offen), 'mb-3 hover:text-akzent', offen ? 'mx-2' : 'mx-1.5')}
          >
            <ExternalLink className="h-4 w-4 shrink-0" />
            {offen && <span className="truncate">{portalLabel ?? 'Portal'}</span>}
          </a>
        </MitNamen>
      )}
    </aside>
  );
}
